'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import type { BlogPost, BlogSettings } from '@/data/blogPosts';
import type { SiteContent } from '@/data/siteContent';
import NavItem from '@/components/layout/NavItem';
import HomeSection from '@/components/sections/HomeSection';
import {
  Home,
  User,
  Lightbulb,
  Briefcase,
  Newspaper,
  Mail,
  FileText,
} from '@/components/ui/Icons';

const AboutSection = dynamic(() => import('@/components/sections/AboutSection'));
const SkillsSection = dynamic(() => import('@/components/sections/SkillsSection'));
const ProjectsSection = dynamic(() => import('@/components/sections/ProjectsSection'));
const BlogSection = dynamic(() => import('@/components/sections/BlogSection'));
const ResumeSection = dynamic(() => import('@/components/sections/ResumeSection'));
const ContactSection = dynamic(() => import('@/components/sections/ContactSection'));
const FloatingContactButton = dynamic(() => import('@/components/layout/FloatingContactButton'));
const AssistantPopup = dynamic(() => import('@/components/assistant/AssistantPopup'));

export default function PortfolioPageClient({
  siteContent,
  blogPosts,
  blogSettings,
}: {
  siteContent: SiteContent;
  blogPosts: BlogPost[];
  blogSettings: BlogSettings;
}) {
  const [activeSection, setActiveSection] = useState('home');
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [shouldRenderAssistantUi, setShouldRenderAssistantUi] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mainContentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const mainEl = mainContentRef.current;
    if (!mainEl) return;

    let frameId: number | null = null;

    const onScroll = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const isDesktopViewport = window.matchMedia('(min-width: 768px)').matches;
        const scope = isDesktopViewport ? mainEl : document;
        const sections = Array.from(scope.querySelectorAll<HTMLElement>('section[id]'));
        if (!sections.length) return;

        const probeY = isDesktopViewport
          ? mainEl.getBoundingClientRect().top + mainEl.getBoundingClientRect().height * 0.35
          : window.innerHeight * 0.35;

        const sectionAtProbe = sections.find((section) => {
          const rect = section.getBoundingClientRect();
          return rect.top <= probeY && rect.bottom >= probeY;
        });

        if (sectionAtProbe) {
          setActiveSection(sectionAtProbe.id);
          return;
        }

        const passedSections = sections.filter((section) => section.getBoundingClientRect().top <= probeY);
        const fallbackSection = passedSections.at(-1) ?? sections[0];
        setActiveSection(fallbackSection.id);
      });
    };

    mainEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      mainEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const handleSetActiveSection = (sectionId: string) => {
    mainContentRef.current?.querySelector(`#${sectionId}`)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMobileNavClick = (sectionId: string) => {
    handleSetActiveSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  const isButtonVisible = activeSection !== 'home';

  useEffect(() => {
    if (isButtonVisible || isAssistantOpen) {
      setShouldRenderAssistantUi(true);
    }
  }, [isAssistantOpen, isButtonVisible]);

  return (
    <div className="relative flex min-h-screen w-full max-w-full flex-col overflow-x-clip bg-transparent font-inter text-gray-100 md:h-screen md:flex-row">
      <nav className="fixed inset-y-0 left-0 hidden w-20 flex-col items-center justify-center space-y-6 rounded-r-2xl bg-black/60 p-4 shadow-2xl md:flex">
        <NavItem icon={<Home className="h-6 w-6" />} label="Inicio" section="home" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<User className="h-6 w-6" />} label="Sobre Mí" section="about" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<Lightbulb className="h-6 w-6" />} label="Habilidades" section="skills" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<Briefcase className="h-6 w-6" />} label="Proyectos" section="projects" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<Newspaper className="h-6 w-6" />} label="Blog" section="blog" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<FileText className="h-6 w-6" />} label="Currículum" section="resume" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
        <NavItem icon={<Mail className="h-6 w-6" />} label="Contacto" section="contact" activeSection={activeSection} setActiveSection={handleSetActiveSection} />
      </nav>

      <div className="fixed left-0 right-0 top-0 z-[60] flex h-16 items-center justify-end border-b border-green-900/30 bg-black/10 px-6 backdrop-blur-md md:hidden">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-gray-300 drop-shadow-md transition-colors hover:text-green-400 focus:outline-none"
          aria-label="Menú"
        >
          {isMobileMenuOpen ? (
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      <div
        className={`fixed inset-0 z-[55] flex flex-col items-center justify-center bg-black/10 backdrop-blur-md transition-all duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
        }`}
      >
        <div className="flex w-full flex-col items-center space-y-6">
          <NavItem icon={<Home className="h-7 w-7" />} label="Inicio" section="home" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<User className="h-7 w-7" />} label="Sobre Mí" section="about" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<Lightbulb className="h-7 w-7" />} label="Habilidades" section="skills" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<Briefcase className="h-7 w-7" />} label="Proyectos" section="projects" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<Newspaper className="h-7 w-7" />} label="Blog" section="blog" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<FileText className="h-7 w-7" />} label="Currículum" section="resume" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
          <NavItem icon={<Mail className="h-7 w-7" />} label="Contacto" section="contact" activeSection={activeSection} setActiveSection={handleMobileNavClick} />
        </div>
      </div>

      <main ref={mainContentRef} className="custom-scrollbar relative z-10 min-w-0 flex-1 overflow-x-clip overflow-y-visible pt-16 md:ml-20 md:h-full md:min-h-0 md:min-w-0 md:overflow-y-auto md:scroll-smooth md:pt-0">
        <div className="mx-auto w-full max-w-full p-4">
          <HomeSection setActiveSection={handleSetActiveSection} hero={siteContent.hero} />
          <div className="deferred-section-shell">
            <AboutSection />
          </div>
          <div className="deferred-section-shell">
            <SkillsSection />
          </div>
          <div className="deferred-section-shell">
            <ProjectsSection />
          </div>
          <div className="deferred-section-shell">
            <BlogSection posts={blogPosts} settings={blogSettings} />
          </div>
          <div className="deferred-section-shell">
            <ResumeSection resumeIntro={siteContent.resume.introText} resumeUrl={siteContent.contact.resumeUrl} />
          </div>
          <div className="deferred-section-shell">
            <ContactSection contact={siteContent.contact} footer={siteContent.footer} />
          </div>
        </div>
      </main>

      {shouldRenderAssistantUi ? (
        <>
          <FloatingContactButton isVisible={isButtonVisible} onAssistantClick={() => setIsAssistantOpen(!isAssistantOpen)} />
          <AssistantPopup isOpen={isAssistantOpen} onClose={() => setIsAssistantOpen(false)} />
        </>
      ) : null}
    </div>
  );
}
